import React, { useRef, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ArrowRight, ArrowLeft, Loader2, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import api from '../utils/api';
import AuthShell, { AuthHeading, FormAlert } from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';

const OFFLINE = "We couldn't reach Rostery. Check your connection, then try again.";

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPw]       = useState('');
  const [loading, setLoading]   = useState(false);
  // MFA step state
  const [mfaStep, setMfaStep]   = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode]   = useState('');
  // Lost phone: sign in with one of the one-time recovery codes instead.
  const [useRecovery, setUseRecovery] = useState(false);
  // Errors are shown where the person is looking, not in a corner toast: per field, plus one
  // alert above the button for a problem with the whole attempt.
  const [fieldErr, setFieldErr] = useState({});
  const [formErr, setFormErr]   = useState(null); // { text, credentials?, restart? }
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const codeRef = useRef(null);
  const { login, setAuthFromToken } = useAuth();
  const navigate = useNavigate();
  // Where to land after a successful login. `next` is only ever an in-app path — an
  // absolute URL here would be an open redirect, so anything not starting with a single
  // slash is discarded.
  const afterLogin = (role) => {
    const raw = new URLSearchParams(window.location.search).get('next') || '';
    const safe = /^\/(?!\/)/.test(raw) ? raw : null;
    if (safe) return safe;
    return role === 'super_admin' ? '/superadmin' : '/';
  };

  // An admin who chose a plan at signup but has never paid goes straight to the Stripe
  // payment page on first sign-in — not to the billing screen to pick a plan they already
  // picked. The server rebuilds the session from what was stored at signup, so nothing is
  // re-chosen and no price crosses the wire.
  //
  // Returns a destination, or null when the browser has already been sent to Stripe.
  const billingLanding = async (role, dest) => {
    if (dest !== '/' || (role !== 'admin' && role !== 'super_admin')) return dest;
    // Once per browser session. Cancelling at Stripe returns them to /billing, and being
    // thrown back to the payment page on every navigation would be a trap, not a prompt.
    if (sessionStorage.getItem('billingPrompted') === '1') return dest;
    sessionStorage.setItem('billingPrompted', '1');
    try {
      const { data } = await api.get('/billing/subscription');
      if (data?.subscription?.status !== 'incomplete') return dest;
      const { data: session } = await api.post('/billing/checkout', { resume: true });
      if (session?.url) { window.location.href = session.url; return null; }
      // Stripe unreachable or unconfigured: the billing page still explains the situation
      // and offers a way forward, which is better than dropping them on the dashboard.
      return '/billing';
    } catch {
      return '/billing';   // never block a sign-in over billing
    }
  };

  const [searchParams] = useSearchParams();
  const [mobileOnly, setMobileOnly] = useState(searchParams.get('mobile') === '1');

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = {};
    if (!email.trim()) errs.email = 'Enter your email address.';
    if (!password) errs.password = 'Enter your password.';
    setFieldErr(errs);
    setFormErr(null);
    if (errs.email) { emailRef.current?.focus(); return; }
    if (errs.password) { passwordRef.current?.focus(); return; }

    setLoading(true);
    setMobileOnly(false);
    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaStep(true);
      } else {
        // Staff / carers use the mobile app — block web access
        if (result?.role === 'carer' || result?.role === 'staff') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setMobileOnly(true);
          return;
        }
        {
          const dest = await billingLanding(result?.role, afterLogin(result?.role));
          if (dest) navigate(dest);
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || '';
      if (!err.response) {
        setFormErr({ text: OFFLINE });
      } else if (status === 401) {
        setFormErr({ text: "That email and password don't match. Check them, or reset your password.", credentials: true });
        emailRef.current?.focus();
      } else if (status === 400 && /email/i.test(msg)) {
        setFieldErr({ email: 'Enter an email address like name@example.com.au.' });
        emailRef.current?.focus();
      } else {
        // Locked account, access turned off, trial ended — the server's own words are specific.
        setFormErr({ text: msg || "We couldn't sign you in. Try again in a moment." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async e => {
    e.preventDefault();
    setFormErr(null);
    if (useRecovery ? mfaCode.replace(/[\s-]/g, '').length !== 10 : mfaCode.length !== 6) {
      setFieldErr({ code: useRecovery
        ? 'Enter one of your recovery codes, like abcde-fghjk.'
        : 'Enter the 6-digit code from your authenticator app.' });
      codeRef.current?.focus();
      return;
    }
    setFieldErr({});
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/mfa/verify',
        useRecovery ? { mfaToken, recoveryCode: mfaCode } : { mfaToken, code: mfaCode });
      // Store token + user via auth context
      if (setAuthFromToken) {
        setAuthFromToken(data.token, data.user);
      } else {
        localStorage.setItem('token', data.token);
      }
      {
        const dest = await billingLanding(data.user.role, afterLogin(data.user.role));
        if (dest) navigate(dest);
      }
    } catch (err) {
      const msg = err.response?.data?.message || '';
      if (!err.response) {
        setFormErr({ text: OFFLINE });
      } else if (/session expired|log in again/i.test(msg)) {
        setFormErr({ text: 'This sign-in timed out. Go back and sign in again.', restart: true });
      } else if (err.response.status === 429) {
        setFormErr({ text: msg || 'Too many attempts. Wait 15 minutes, then try again.' });
      } else {
        setFieldErr({ code: useRecovery
          ? "That recovery code didn't work. Each code can only be used once."
          : "That code didn't work. Codes change every 30 seconds." });
        codeRef.current?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setMfaStep(false); setMfaCode(''); setMfaToken(''); setUseRecovery(false); setFieldErr({}); setFormErr(null);
  };

  const toggleRecovery = () => {
    setUseRecovery(r => !r); setMfaCode(''); setFieldErr({}); setFormErr(null);
    setTimeout(() => codeRef.current?.focus(), 0);
  };

  const credentialsWrong = !!formErr?.credentials;

  return (
    <AuthShell>
      {/* Staff and carers sign in on the phone app */}
      {mobileOnly && (
        <div role="status" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <Smartphone size={18} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Use the Rostery app</p>
              <p className="text-[13px] text-amber-800 mt-0.5 leading-relaxed">
                This account is for staff and carers. Sign in through the Rostery mobile app instead.
              </p>
              <button type="button" onClick={() => setMobileOnly(false)}
                className="mt-2 text-[13px] font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {!mfaStep ? (
        /* ── Password step ── */
        <div>
          <AuthHeading title="Welcome back">Sign in to your workspace.</AuthHeading>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <AuthField
              ref={emailRef}
              label="Email address"
              icon={Mail}
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); if (fieldErr.email) setFieldErr(f => ({ ...f, email: null })); }}
              placeholder="you@example.com"
              autoComplete="email"
              error={fieldErr.email}
              invalid={credentialsWrong}
              describedBy={credentialsWrong ? 'login-error' : undefined}
            />

            <AuthField
              ref={passwordRef}
              label="Password"
              icon={Lock}
              type="password"
              value={password}
              onChange={e => { setPw(e.target.value); if (fieldErr.password) setFieldErr(f => ({ ...f, password: null })); }}
              placeholder="••••••••"
              autoComplete="current-password"
              error={fieldErr.password}
              invalid={credentialsWrong}
              describedBy={credentialsWrong ? 'login-error' : undefined}
              action={(
                <Link to="/forgot-password"
                  className="inline-flex items-center py-1.5 -my-1.5 text-[13px] font-medium text-primary-700 hover:text-primary-800">
                  Forgot password?
                </Link>
              )}
            />

            {formErr && (
              <FormAlert id="login-error">
                {formErr.text}
                {formErr.credentials && (
                  <>
                    {' '}
                    <Link to="/forgot-password" className="font-semibold underline underline-offset-2">Reset your password</Link>
                  </>
                )}
              </FormAlert>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-sm">
              {loading
                ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Signing in…</>
                : <>Sign in <ArrowRight size={15} aria-hidden="true" /></>}
            </button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-6 leading-relaxed">
            By signing in you agree to the{' '}
            <Link to="/terms" className="underline underline-offset-2 hover:text-slate-700">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-slate-700">Privacy Policy</Link>.
          </p>
        </div>
      ) : (
        /* ── MFA step ── */
        <div>
          <AuthHeading title="Two-factor verification">
            {useRecovery
              ? 'Enter one of the recovery codes you saved when you set up two-factor. Each code works once.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </AuthHeading>

          <form onSubmit={handleMfaVerify} noValidate className="space-y-4">
            {useRecovery ? (
              <AuthField
                key="recovery"
                ref={codeRef}
                label="Recovery code"
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={mfaCode}
                onChange={e => { setMfaCode(e.target.value.replace(/[^a-zA-Z0-9\s-]/g, '').slice(0, 13)); if (fieldErr.code) setFieldErr({}); }}
                inputClassName="text-center text-xl tracking-[0.2em] font-mono"
                placeholder="xxxxx-xxxxx"
                maxLength={13}
                autoFocus
                error={fieldErr.code}
              />
            ) : (
              <AuthField
                key="totp"
                ref={codeRef}
                label="Authentication code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={e => { setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (fieldErr.code) setFieldErr({}); }}
                inputClassName="text-center text-2xl tracking-[0.4em] font-mono"
                placeholder="000000"
                maxLength={6}
                autoFocus
                error={fieldErr.code}
              />
            )}

            {formErr && <FormAlert>{formErr.text}</FormAlert>}

            {!formErr?.restart && (
              <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-sm">
                {loading
                  ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Verifying…</>
                  : <>Verify <ArrowRight size={15} aria-hidden="true" /></>}
              </button>
            )}

            {!formErr?.restart && (
              <button
                type="button"
                onClick={toggleRecovery}
                className="w-full inline-flex items-center justify-center min-h-[40px] text-[13px] font-medium text-primary-700 hover:text-primary-800"
              >
                {useRecovery ? 'Use the code from your authenticator app' : 'Lost your phone? Use a recovery code'}
              </button>
            )}

            <button
              type="button"
              onClick={backToPassword}
              className={formErr?.restart
                ? 'btn-primary w-full py-3 text-sm'
                : 'w-full inline-flex items-center justify-center gap-1.5 min-h-[40px] text-sm text-slate-600 hover:text-slate-900'}
            >
              <ArrowLeft size={15} aria-hidden="true" /> Back to sign in
            </button>
          </form>
        </div>
      )}
    </AuthShell>
  );
}
